import React, { useMemo } from 'react';
import { Box, Tooltip } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { alpha, useTheme } from '@mui/material/styles';
import { dateTimeFormatter } from 'src/libs/dateTimeFormatter';

interface FollowUpIndicatorProps {
  /** ISO timestamp for the scheduled follow-up */
  futureTime: string;
  /** Optional size of the base icon in pixels (defaults to 20) */
  size?: number;
  /** Optional click handler */
  onClick?: () => void;
}

/**
 * Tiny, sleek indicator that a follow-up is scheduled for the future.
 * Shows an Access-time icon with a small badge displaying the remaining
 * days / hours / minutes until the follow-up. Hovering reveals the exact
 * scheduled time, formatted via `dateTimeFormatter`.
 * 
 * No polling - purely reactive to futureTime prop changes.
 */
const FollowUpIndicator: React.FC<FollowUpIndicatorProps> = ({
  futureTime,
  size = 20,
  onClick,
}) => {
  const theme = useTheme();

  const timeLeftMs = useMemo(() => {
    return Math.max(0, new Date(futureTime).getTime() - Date.now());
  }, [futureTime]);

  if (isNaN(timeLeftMs) || timeLeftMs <= 0) return null; // nothing scheduled or already passed

  const minutes = Math.floor(timeLeftMs / 60000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  let badgeLabel: string;
  if (days > 0) {
    badgeLabel = `${days}d`;
  } else if (hours > 0) {
    badgeLabel = `${hours}h`;
  } else {
    badgeLabel = `${minutes}m`;
  }

  const formattedFullDate = dateTimeFormatter(futureTime, 'day_month_ordinal_humanized_time_period');

  const badgeBg = alpha(theme.palette.mode === 'dark' ? theme.palette.primary.light || '#90caf9' : theme.palette.primary.main, 0.9);
  const badgeTextColor = theme.palette.getContrastText(badgeBg);

  return (
    <Tooltip title={`Follow-up @ ${formattedFullDate}`}>        
      <Box
        onClick={onClick}
        sx={{
          position: 'relative',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: size,
          height: size,
          cursor: onClick ? 'pointer' : 'default',
        }}
      >
        <AccessTimeIcon sx={{ fontSize: size, color: 'text.secondary' }} />
        {/* Badge */}
        <Box
          sx={{
            position: 'absolute',
            bottom: -2,
            right: -2,
            width: size / 2,
            height: size / 2,
            borderRadius: '50%',
            backgroundColor: badgeBg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 0.5,
          }}
        >
          <Box
            component="span"
            sx={{
              fontSize: size / 3,
              color: badgeTextColor,
              lineHeight: 1,
              fontWeight: 600,
            }}
          >
            {badgeLabel}
          </Box>
        </Box>
      </Box>
    </Tooltip>
  );
};

export default FollowUpIndicator; 
